import { useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router } from '@inertiajs/react';
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type BoardMember = {
  id: number;
  name: string;
  role: string;
  bio: string;
  image_url: string;
  email?: string;
  linkedin?: string;
  website?: string;
  category: string;
  board_type: string;
  position: number;
};

type Props = {
  board_members: BoardMember[];
};

export default function BoardMembersIndex({ board_members }: Props) {
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const boardTypeLabels = {
    executive: 'Executive Board',
    directors: 'Board of Directors',
  };

  const categoryLabels = {
    executive: 'Executive',
    pre_grad: 'Pre-Grad',
    pre_health: 'Pre-Health',
  };

  const groupedMembers = board_members.reduce(
    (acc, member) => {
      // Group by board_type only for directors, by board_type-category for executive
      const key =
        member.board_type === 'directors'
          ? member.board_type
          : `${member.board_type}-${member.category}`;

      if (!acc[key]) {
        acc[key] = {
          board_type: member.board_type,
          category: member.category,
          members: [],
        };
      }
      acc[key].members.push(member);
      return acc;
    },
    {} as Record<
      string,
      { board_type: string; category: string; members: BoardMember[] }
    >
  );

  const handleDelete = () => {
    if (deleteId) {
      router.delete(`/admin/board_members/${deleteId}`, {
        onSuccess: () => setDeleteId(null),
      });
    }
  };

  return (
    <AdminLayout title="Board Members">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Board Members
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage executive board and director information
            </p>
          </div>
          <Button asChild>
            <Link href="/admin/board_members/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Board Member
            </Link>
          </Button>
        </div>

        {/* Board Members by Board Type and Category */}
        {Object.entries(groupedMembers)
          .sort(([, a], [, b]) => {
            // Sort executive board first, then directors
            if (a.board_type === 'executive' && b.board_type !== 'executive')
              return -1;
            if (a.board_type !== 'executive' && b.board_type === 'executive')
              return 1;
            return 0;
          })
          .map(([key, group]) => (
            <div key={key} className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  {
                    boardTypeLabels[
                      group.board_type as keyof typeof boardTypeLabels
                    ]
                  }
                  {group.board_type === 'executive' &&
                    group.category !== 'executive' &&
                    ` - ${categoryLabels[group.category as keyof typeof categoryLabels]}`}
                </h2>
              </div>
              <div className="p-6">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {group.members.map((member) => (
                    <div
                      key={member.id}
                      className="flex flex-col bg-gray-50 rounded-lg p-4 border border-gray-200"
                    >
                      <div className="flex items-start gap-4 mb-3">
                        <img
                          src={member.image_url}
                          alt={member.name}
                          className="w-16 h-16 rounded-full object-cover bg-white"
                        />
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">
                            {member.name}
                          </h3>
                          <p className="text-sm text-gray-600">{member.role}</p>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700 mt-1">
                            {
                              boardTypeLabels[
                                member.board_type as keyof typeof boardTypeLabels
                              ]
                            }
                          </span>
                        </div>
                      </div>

                      {member.bio && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {member.bio}
                        </p>
                      )}

                      <div className="flex items-center gap-3 mb-3">
                        {member.email && (
                          <a
                            href={`mailto:${member.email}`}
                            className="text-gray-400 hover:text-gray-600"
                            title="Email"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                          </a>
                        )}
                        {member.linkedin && (
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-gray-600"
                            title="LinkedIn"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                            </svg>
                          </a>
                        )}
                        {member.website && (
                          <a
                            href={member.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-gray-600"
                            title="Website"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>

                      <div className="flex gap-2 mt-auto">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          asChild
                        >
                          <Link href={`/admin/board_members/${member.id}/edit`}>
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Link>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(member.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Board Member</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this board member? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
